import type { GoalDto } from "@home/shared";
import { useState } from "react";
import { api } from "../api.ts";
import { EmptyState, SkeletonCard } from "../components/EmptyState.tsx";
import { NewGoalSheet } from "../components/Goals.tsx";
import { KidGoalsCard, PocketHistory, RecordSheet, SaveSheet, TipCard, WalletHero } from "../components/Pocket.tsx";
import { t, type Lang } from "../i18n.ts";
import { useSync } from "../store.ts";
import { hapticSelect } from "../telegram.ts";
import { useRemote } from "../useRemote.ts";

type Open = { kind: "spend" | "gift" } | { kind: "save"; goal: GoalDto | null } | { kind: "newGoal" };

/** Вкладка ребёнка «Мои деньги»: кошелёк, копилки, совет недели и история. */
export function PocketPage({ childId, lang }: { childId: string; lang: Lang }) {
  const remote = useRemote(`pocket:${childId}`, () => api.pocket(childId));
  const [open, setOpen] = useState<Open | null>(null);
  const w = remote.data;

  if (!w) {
    return (
      <main className="dash">
        {remote.loading ? (
          <>
            <SkeletonCard lines={3} />
            <SkeletonCard lines={4} />
          </>
        ) : (
          <p className="empty">{t("loadError")}</p>
        )}
      </main>
    );
  }

  const show = (next: Open) => {
    hapticSelect();
    setOpen(next);
  };
  // Отложить некуда — сначала завести копилку.
  const save = () => show(w.goals.some((g) => g.currency === "UZS") ? { kind: "save", goal: null } : { kind: "newGoal" });
  const fresh = w.entries.length === 0 && w.balance === 0;

  return (
    <main className="dash">
      <WalletHero wallet={w} lang={lang}>
        <div className="pocket-actions">
          <button type="button" className="pocket-action" onClick={() => show({ kind: "spend" })}>
            <span aria-hidden="true">💸</span>
            {t("spendBtn")}
          </button>
          <button type="button" className="pocket-action" onClick={save}>
            <span aria-hidden="true">🐷</span>
            {t("saveBtn")}
          </button>
          <button type="button" className="pocket-action" onClick={() => show({ kind: "gift" })}>
            <span aria-hidden="true">🎁</span>
            {t("giftBtn")}
          </button>
        </div>
      </WalletHero>
      <KidGoalsCard wallet={w} lang={lang} onPick={(goal) => show({ kind: "save", goal })} />
      <TipCard lang={lang} />
      {fresh ? (
        <section className="dash-card">
          <EmptyState icon="👛" title={t("pocketEmpty")} text={t("pocketEmptyHint")} />
        </section>
      ) : (
        <PocketHistory wallet={w} lang={lang} />
      )}

      {open && (open.kind === "spend" || open.kind === "gift") && <RecordSheet childId={childId} kind={open.kind} lang={lang} onClose={() => setOpen(null)} />}
      {open?.kind === "save" && <SaveSheet wallet={w} goal={open.goal} lang={lang} onClose={() => setOpen(null)} />}
      {open?.kind === "newGoal" && <NewGoalSheet lang={lang} onClose={() => setOpen(null)} />}
    </main>
  );
}
